import React, { createContext, ReactNode, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { FAQsImage } from '../components/HelpEditor/Images/ImagesDashboard';
import * as sourceServices from '../services/sourceService';

interface ImagesContextType {
	images: FAQsImage[] | undefined;
	isLoading: boolean;
	error: string | null;
	refetchImages: () => Promise<void>;
	sortedImages: FAQsImage[];
}

export const ImagesContext = createContext<ImagesContextType>({
	images: undefined,
	isLoading: false,
	error: null,
	refetchImages: async () => {},
	sortedImages: [],
});

interface ImagesProviderProps {
	children: ReactNode;
}

export const ImagesProvider: React.FC<ImagesProviderProps> = ({ children }) => {
	const [error, setError] = useState<string | null>(null);

	const {
		data: images,
		isLoading,
		refetch,
	} = useQuery<FAQsImage[]>({
		queryKey: ['faqsMediaImages'],
		queryFn: async () => {
			try {
				return sourceServices.getFaqsMediaImages();
			} catch (error) {
				setError('Failed to load images');
				throw error;
			}
		},
		staleTime: Infinity, // Don't refetch automatically
	});

	const refetchImages = useCallback(async () => {
		try {
			await refetch();
			setError(null);
		} catch (err) {
			setError('Failed to refresh images');
		}
	}, [refetch]);

	// Sort images by last modified date (newest first)
	const sortedImages = images
		? [...images].sort((a, b) => {
				const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
				const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
				return dateB - dateA;
			})
		: [];

	return (
		<ImagesContext.Provider
			value={{
				images,
				isLoading,
				error,
				refetchImages,
				sortedImages,
			}}
		>
			{children}
		</ImagesContext.Provider>
	);
};

export const useImages = () => React.useContext(ImagesContext);
